import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDrawingStore } from "../../store/useDrawingStore";
import { ActiveView, useUIStore } from "../../store/useUIStore";
import { useModelStore } from "../../store/useModelStore";
import { useCursorMode } from "@/hooks/useGlobalSelection";
import { usePropertyStore } from "@/store/usePropertyStore";
import { useGlobalSelectionStore } from "@/store/useGlobalSelectionStore";
import { SCALE_RANGES } from "@/utils/gridScale";

const ViewMenu: React.FC = () => {
  const scaleRange = useUIStore((s) => s.scaleRange);
  const setScaleRange = useUIStore((s) => s.setScaleRange);
  const [autoScale, setAutoScale] = useState<boolean>(() => {
    const stored = localStorage.getItem("auto_scale_enabled");
    return stored === null ? true : stored === "true";
  });

  const activeView = useUIStore((s) => s.activeView);
  const setActiveView = useUIStore((s) => s.setActiveView);
  const [showBeams, setShowBeams] = useState(true);
  const [showNodes, setShowNodes] = useState(true);
  const [showGlobalAxis, setShowGlobalAxis] = useState(true);
  const [showLocalAxis, setShowLocalAxis] = useState(false);
  const activeSelectionMode = useUIStore((s) => s.activeSelectionMode);
  const setActiveSelectionMode = useUIStore((s) => s.setActiveSelectionMode);

  const { setCursorMode, clearCursorMode } = useCursorMode();

  const cursorModeTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Local state for zoom input (doesn't apply until blur/enter)
  const [zoomInputValue, setZoomInputValue] = useState<string>("");
  const [isZoomInputFocused, setIsZoomInputFocused] = useState(false);

  const uiStore = useUIStore();
  const showNodeLabels = uiStore.showNodeIds;
  const showBeamLabels = uiStore.showBeamIds;

  const zoom = useDrawingStore((state) => state.viewTransform.scale);
  const gridVisible = useDrawingStore((state) => state.showGrid);
  const toggleGrid = useDrawingStore((state) => state.toggleGrid);

  const zoomLevel = Math.round(zoom * 100);

  useEffect(() => {
    const stored = localStorage.getItem("auto_scale_enabled");
    if (stored !== null) {
      setAutoScale(stored === "true");
    }
  }, []);

  const toggleAutoScale = () => {
    setAutoScale((prev) => {
      const next = !prev;
      localStorage.setItem("auto_scale_enabled", String(next));
      // Trigger recalculation when auto scale is enabled
      if (next) {
        window.dispatchEvent(new CustomEvent("triggerAutoScaleRecalculation"));
      }
      return next;
    });
  };
  const activeCursorMode = useGlobalSelectionStore(
    (state) => state.activeCursorMode
  );

  useEffect(() => {
    return () => {
      if (cursorModeTimerRef.current) {
        clearTimeout(cursorModeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // If cursor mode is NOT a ViewMenu selection mode, clear the timer
    const isSelectionMode =
      activeCursorMode === "select-nodes" ||
      activeCursorMode === "select-beams" ||
      activeCursorMode === "select-all";

    if (!isSelectionMode && cursorModeTimerRef.current) {
      console.log(
        "🔄 Clearing ViewMenu timer - switched to:",
        activeCursorMode
      );
      clearTimeout(cursorModeTimerRef.current);
      cursorModeTimerRef.current = null;
    }
  }, [activeCursorMode]);

  // ESC key handler to clear selection mode and cursor
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();

        if (cursorModeTimerRef.current) {
          clearTimeout(cursorModeTimerRef.current);
          cursorModeTimerRef.current = null;
        }

        // 1. Clear ViewMenu selection (existing)
        clearCursorMode();
        setActiveSelectionMode("all");
        (window as any).__currentSelectionMode = null;
        useModelStore.setState({
          selectedNodeIds: [],
          selectedBeamIds: [],
        });

        // 2. Cancel ALL table apply-by-click modes (like ModelingMenu)
        useGlobalSelectionStore.getState().clearTableSelection(); // Clears propertyStore.applyByClickMode
        usePropertyStore.getState().setApplyByClickMode(false);
        usePropertyStore.getState().setActivePropertyIdForClick(null);

        // 3. Cancel other global modes
        window.dispatchEvent(new CustomEvent("cancelLoadcaseApplyModes"));
        window.dispatchEvent(new CustomEvent("cancelSupportsApplyModes"));
        window.dispatchEvent(new CustomEvent("cancelPropertyApplyModes"));
      }
    };
    window.addEventListener("keydown", handleEscapeKey);
    return () => window.removeEventListener("keydown", handleEscapeKey);
  }, [clearCursorMode]);

  const clickToolbarButton = useCallback((searchTexts: string[]) => {
    const buttons = document.querySelectorAll("button");
    for (const text of searchTexts) {
      const button = Array.from(buttons).find((btn) => {
        const btnText = btn.textContent?.trim();
        const isMenuButton = btn.closest('[class*="bg-gray-8"]') !== null;
        if (isMenuButton) return false;
        return btnText === text || btnText?.includes(text);
      });
      if (button) {
        button.click();
        return true;
      }
    }
    return false;
  }, []);

  const disableAllModes = useCallback(() => {
    const modelStore = useModelStore.getState();

    // Cancel modes directly via store - DO NOT click toolbar buttons (that toggles them ON!)
    if (modelStore.isPrepMode) modelStore.cancelPrepMode();
    if (modelStore.cancelBeamCreation) modelStore.cancelBeamCreation();

    // Cancel node creation mode if it exists
    if (modelStore.isCreatingNode) {
      useModelStore.setState({ isCreatingNode: false });
    }

    const propStore = usePropertyStore.getState();
    propStore.setApplyByClickMode(false);
    propStore.setActivePropertyIdForClick(null);

    // Dispatch cancel events to notify other components
    window.dispatchEvent(new CustomEvent("cancelLoadcaseApplyModes"));
    window.dispatchEvent(new CustomEvent("cancelSupportsApplyModes"));
    window.dispatchEvent(new CustomEvent("cancelSelectMode"));
    window.dispatchEvent(new CustomEvent("cancelPropertyApplyModes"));
    window.dispatchEvent(new CustomEvent("cancelNodeCreation"));
    window.dispatchEvent(new CustomEvent("cancelBeamCreation"));

    clearCursorMode();
  }, [clearCursorMode]);

  const handleViewClick = useCallback(
    (view: ActiveView, searchTexts: string[]) => {
      setActiveView(view);
      clickToolbarButton(searchTexts);
    },
    [clickToolbarButton, setActiveView]
  );

  const handleGridToggle = useCallback(() => {
    toggleGrid();
    clickToolbarButton(["Show Grid", "Hide Grid", "Grid"]);
  }, [toggleGrid, clickToolbarButton]);

  const handleNodeLabelsToggle = useCallback(() => {
    uiStore.toggleNodeIds();
  }, [uiStore]);

  const handleBeamLabelsToggle = useCallback(() => {
    uiStore.toggleBeamIds();
  }, [uiStore]);

  const deactivateSelectionMode = useCallback(() => {
    setActiveSelectionMode(null);
    // Clear the global selection mode so drag selection uses no filter
    (window as any).__currentSelectionMode = null;

    if (cursorModeTimerRef.current) {
      clearTimeout(cursorModeTimerRef.current);
      cursorModeTimerRef.current = null;
    }

    // Clear visual highlighting
    clearCursorMode();

    // Clear selection in store
    useModelStore.setState({
      selectedNodeIds: [],
      selectedBeamIds: [],
    });
  }, [setActiveSelectionMode, clearCursorMode]);

  const setCursorModeWithTimer = useCallback(
    (mode: "select-nodes" | "select-beams" | "select-all") => {
      // Clear any existing timer
      if (cursorModeTimerRef.current) {
        clearTimeout(cursorModeTimerRef.current);
      }

      // Set the cursor mode
      setCursorMode(mode);

      // Set new timer to auto-dismiss after 5 seconds
      cursorModeTimerRef.current = setTimeout(() => {
        clearCursorMode();
        cursorModeTimerRef.current = null;
      }, 5000); // 5 seconds
    },
    [setCursorMode, clearCursorMode]
  );

  const handleSelectNodes = useCallback(() => {
    if (activeSelectionMode === "nodes") {
      // Already on nodes → Switch to all
      setActiveSelectionMode("all");
      (window as any).__currentSelectionMode = "all";
      setCursorModeWithTimer("select-all");
      console.log("🎯 Selection mode toggled to: all");
    } else {
      // Not on nodes → Switch to nodes
      setActiveSelectionMode("nodes");
      disableAllModes();
      (window as any).__currentSelectionMode = "nodes";
      setCursorModeWithTimer("select-nodes");
      console.log("🎯 Selection mode set to: nodes");
    }
  }, [
    activeSelectionMode,
    disableAllModes,
    setActiveSelectionMode,
    setCursorModeWithTimer,
  ]);

  // ✅ NEW: Beams button - toggles between "beams" and "all"
  const handleSelectBeams = useCallback(() => {
    if (activeSelectionMode === "beams") {
      // Already on beams → Switch to all
      setActiveSelectionMode("all");
      (window as any).__currentSelectionMode = "all";
      setCursorModeWithTimer("select-all");
      console.log("🎯 Selection mode toggled to: all");
    } else {
      // Not on beams → Switch to beams
      setActiveSelectionMode("beams");
      disableAllModes();
      (window as any).__currentSelectionMode = "beams";
      setCursorModeWithTimer("select-beams");
      console.log("🎯 Selection mode set to: beams");
    }
  }, [
    activeSelectionMode,
    disableAllModes,
    setActiveSelectionMode,
    setCursorModeWithTimer,
  ]);

  // ✅ NEW: All button - always stays on "all" (no toggle)
  const handleSelectAll = useCallback(() => {
    // Always set to "all" mode (no toggle)
    setActiveSelectionMode("all");
    disableAllModes();
    (window as any).__currentSelectionMode = "all";
    setCursorModeWithTimer("select-all");
    console.log("🎯 Selection mode set to: all");
  }, [disableAllModes, setActiveSelectionMode, setCursorModeWithTimer]);

  const handleClearSelection = useCallback(() => {
    // Clear the global selection mode
    (window as any).__currentSelectionMode = null;
    setActiveSelectionMode("all");

    if (cursorModeTimerRef.current) {
      clearTimeout(cursorModeTimerRef.current);
      cursorModeTimerRef.current = null;
    }

    clearCursorMode();
    useModelStore.setState({
      selectedNodeIds: [],
      selectedBeamIds: [],
    });
  }, [setActiveSelectionMode, clearCursorMode]);

  // // ✅ ROBUST TABLE ROW LISTENER - FIXED
  // useEffect(() => {
  //   const handleTableRowClick = () => {
  //     if (activeSelectionMode) {
  //       // 1. Clear ViewMenu state
  //       setActiveSelectionMode(null);

  //       // 2. Clear global selection mode
  //       (window as any).__currentSelectionMode = null;

  //       // 3. Clear model selection
  //       useModelStore.setState({
  //         selectedNodeIds: [],
  //         selectedBeamIds: [],
  //       });

  //       // 4. Clear global cursor
  //       clearCursorMode();

  //       // 5. ROBUST toolbar reset - dispatch cancel event
  //       window.dispatchEvent(new CustomEvent("cancelSelectMode"));
  //     }
  //   };

  //   // Listen for ALL table interactions
  //   window.addEventListener("property-apply-click", handleTableRowClick);
  //   window.addEventListener("support-apply-click", handleTableRowClick);
  //   window.addEventListener("loadcase-apply-click", handleTableRowClick);

  //   // Generic table row detection
  //   const handleTableClick = (e: MouseEvent) => {
  //     const target = e.target as HTMLElement;
  //     if (
  //       target.closest('table, [role="table"], .table-row') &&
  //       activeSelectionMode
  //     ) {
  //       handleTableRowClick();
  //     }
  //   };

  //   document.addEventListener("click", handleTableClick);

  //   return () => {
  //     window.removeEventListener("property-apply-click", handleTableRowClick);
  //     window.removeEventListener("support-apply-click", handleTableRowClick);
  //     window.removeEventListener("loadcase-apply-click", handleTableRowClick);
  //     document.removeEventListener("click", handleTableClick);
  //   };
  // }, [activeSelectionMode, clearCursorMode, deactivateSelectionMode]);

  return (
    <div className="px-5 py-1 bg-gradient-to-b from-gray-850 to-gray-900">
      <div className="flex items-center gap-5">
        <div className="flex items-center gap-2">
          <div className="text-[10px] text-gray-500 font-semibold writing-mode-vertical h-12 flex items-center tracking-wide">
            SELECT
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex gap-1">
              <button
                onClick={handleSelectNodes}
                className={`flex items-center gap-1 px-2.5 py-0.5 text-xs rounded-md transition-all shadow-sm ${activeSelectionMode === "nodes" ? "bg-gradient-to-br from-sky-500/30 to-sky-600/20 text-sky-200 border border-sky-500/60" : "bg-gray-800 text-gray-300 border border-gray-700 hover:border-sky-600/40 hover:bg-sky-500/20"}`}
              >
                <svg
                  className="w-3.5 h-3.5 text-sky-400"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <circle cx="8" cy="8" r="2" />
                  <circle cx="16" cy="8" r="2" />
                  <circle cx="8" cy="16" r="2" />
                  <circle cx="16" cy="16" r="2" />
                </svg>
                <span className="font-medium">Nodes</span>
              </button>
              <button
                onClick={handleSelectBeams}
                className={`flex items-center gap-1 px-2.5 py-0.5 text-xs rounded-md transition-all shadow-sm ${activeSelectionMode === "beams" ? "bg-gradient-to-br from-emerald-500/30 to-emerald-600/20 text-emerald-200 border border-emerald-500/60" : "bg-gray-800 text-gray-300 border border-gray-700 hover:border-emerald-600/40 hover:bg-emerald-500/20"}`}
              >
                <svg
                  className="w-3.5 h-3.5 text-emerald-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <line x1="4" y1="8" x2="20" y2="8" strokeWidth="2" />
                  <line x1="4" y1="16" x2="20" y2="16" strokeWidth="2" />
                </svg>
                <span className="font-medium">Beams</span>
              </button>
            </div>
            <div className="flex gap-1">
              <button
                onClick={handleSelectAll}
                className={`flex items-center justify-center gap-1 px-2.5 py-0.5 text-xs rounded-md transition-all shadow-sm flex-1 ${activeSelectionMode === "all" ? "bg-gradient-to-br from-blue-500/30 to-blue-600/20 text-blue-200 border border-blue-500/60" : "bg-gray-800 text-gray-300 border border-gray-700 hover:border-blue-600/40 hover:bg-blue-500/20"}`}
              >
                <svg
                  className="w-3.5 h-3.5 text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <rect
                    x="3"
                    y="3"
                    width="18"
                    height="18"
                    strokeWidth="2"
                    rx="2"
                  />
                  <path
                    d="M9 12l2 2 4-4"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="font-medium">All</span>
              </button>
              <button
                onClick={handleClearSelection}
                className="flex items-center justify-center gap-1 px-2.5 py-0.5 text-xs rounded-md transition-all shadow-sm bg-gray-800 text-gray-300 border border-gray-700 hover:border-red-600/40 hover:bg-red-500/20 flex-1"
              >
                <svg
                  className="w-3.5 h-3.5 text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                <span className="font-medium">Clear</span>
              </button>
            </div>
          </div>
        </div>

        <div className="w-px h-12 bg-gradient-to-b from-transparent via-gray-600 to-transparent"></div>

        <div className="flex items-center gap-2">
          <div className="text-[10px] text-gray-500 font-semibold writing-mode-vertical h-12 flex items-center tracking-wide">
            BEAM
          </div>
          <div className="flex flex-col gap-1">
            <button
              onClick={() => setShowBeams(!showBeams)}
              className={`flex items-center gap-1.5 px-2.5 py-0.5 text-xs rounded-md transition-all shadow-sm ${showBeams ? "bg-gradient-to-br from-emerald-500/30 to-emerald-600/20 text-emerald-200 border border-emerald-500/60" : "bg-gray-800 text-gray-400 border border-gray-700 hover:border-emerald-600/40"}`}
            >
              <svg
                className="w-3.5 h-3.5 text-emerald-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <line
                  x1="4"
                  y1="12"
                  x2="20"
                  y2="12"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <circle cx="4" cy="12" r="1.5" fill="currentColor" />
                <circle cx="20" cy="12" r="1.5" fill="currentColor" />
              </svg>
              <span className="font-medium">Beams</span>
            </button>
            <button
              onClick={handleBeamLabelsToggle}
              className={`flex items-center gap-1.5 px-2.5 py-0.5 text-xs rounded-md transition-all shadow-sm ${showBeamLabels ? "bg-gradient-to-br from-emerald-500/30 to-emerald-600/20 text-emerald-200 border border-emerald-500/60" : "bg-gray-800 text-gray-400 border border-gray-700 hover:border-emerald-600/40"}`}
            >
              <svg
                className="w-3.5 h-3.5 text-emerald-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M7 8h4m-4 4h10m-10 4h10"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <span className="font-medium">Labels</span>
            </button>
          </div>
        </div>
        <div className="w-px h-12 bg-gradient-to-b from-transparent via-gray-600 to-transparent"></div>
        <div className="flex items-center gap-2">
          <div className="text-[10px] text-gray-500 font-semibold writing-mode-vertical h-12 flex items-center tracking-wide">
            NODES
          </div>
          <div className="flex flex-col gap-1">
            <button
              onClick={() => setShowNodes(!showNodes)}
              className={`flex items-center gap-1.5 px-2.5 py-0.5 text-xs rounded-md transition-all shadow-sm ${showNodes ? "bg-gradient-to-br from-sky-500/30 to-sky-600/20 text-sky-200 border border-sky-500/60" : "bg-gray-800 text-gray-400 border border-gray-700 hover:border-sky-600/40"}`}
            >
              <svg
                className="w-3.5 h-3.5 text-sky-400"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <circle cx="12" cy="12" r="3.5" />
              </svg>
              <span className="font-medium">Nodes</span>
            </button>
            <button
              onClick={handleNodeLabelsToggle}
              className={`flex items-center gap-1.5 px-2.5 py-0.5 text-xs rounded-md transition-all shadow-sm ${showNodeLabels ? "bg-gradient-to-br from-sky-500/30 to-sky-600/20 text-sky-200 border border-sky-500/60" : "bg-gray-800 text-gray-400 border border-gray-700 hover:border-sky-600/40"}`}
            >
              <svg
                className="w-3.5 h-3.5 text-sky-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <text
                  x="12"
                  y="16"
                  textAnchor="middle"
                  fontSize="14"
                  fill="currentColor"
                >
                  1
                </text>
              </svg>
              <span className="font-medium">Labels</span>
            </button>
          </div>
        </div>
        <div className="w-px h-12 bg-gradient-to-b from-transparent via-gray-600 to-transparent"></div>
        <div className="flex items-center gap-2">
          <div className="text-[10px] text-gray-500 font-semibold writing-mode-vertical h-12 flex items-center tracking-wide">
            VIEWS
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex gap-1">
              <button
                aria-pressed={activeView === "front"}
                onClick={() =>
                  handleViewClick("front", ["Front View", "Front"])
                }
                className={`flex items-center gap-1 px-2.5 py-0.5 text-xs rounded-md transition-all shadow-sm ${activeView === "front" ? "bg-gradient-to-br from-blue-500/30 to-blue-600/20 text-blue-200 border border-blue-500/60" : "bg-gray-800 text-gray-300 border border-gray-700 hover:border-blue-600/40"}`}
              >
                <svg
                  className="w-3.5 h-3.5 text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <rect
                    x="5"
                    y="5"
                    width="14"
                    height="14"
                    strokeWidth="2"
                    rx="1"
                  />
                </svg>
                <span className="font-medium">Front</span>
              </button>

              <button
                aria-pressed={activeView === "top"}
                onClick={() => handleViewClick("top", ["Top View", "Top"])}
                className={`flex items-center gap-1 px-2.5 py-0.5 text-xs rounded-md transition-all shadow-sm ${activeView === "top" ? "bg-gradient-to-br from-green-500/30 to-green-600/20 text-green-200 border border-green-500/60" : "bg-gray-800 text-gray-300 border border-gray-700 hover:border-green-600/40"}`}
              >
                <svg
                  className="w-3.5 h-3.5 text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <rect
                    x="5"
                    y="9"
                    width="14"
                    height="6"
                    strokeWidth="2"
                    rx="1"
                  />
                </svg>
                <span className="font-medium">Top</span>
              </button>

              <button
                aria-pressed={activeView === "side"}
                onClick={() => handleViewClick("side", ["Side View", "Side"])}
                className={`flex items-center gap-1 px-2.5 py-0.5 text-xs rounded-md transition-all shadow-sm ${activeView === "side" ? "bg-gradient-to-br from-purple-500/30 to-purple-600/20 text-purple-200 border border-purple-500/60" : "bg-gray-800 text-gray-300 border border-gray-700 hover:border-purple-600/40"}`}
              >
                <svg
                  className="w-3.5 h-3.5 text-purple-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <rect
                    x="7"
                    y="5"
                    width="10"
                    height="14"
                    strokeWidth="2"
                    rx="1"
                  />
                </svg>
                <span className="font-medium">Side</span>
              </button>

              <button
                aria-pressed={activeView === "isometric"}
                onClick={() =>
                  handleViewClick("isometric", ["Isometric", "Iso"])
                }
                className={`flex items-center gap-1 px-2.5 py-0.5 text-xs rounded-md transition-all shadow-sm ${activeView === "isometric" ? "bg-gradient-to-br from-orange-500/30 to-orange-600/20 text-orange-200 border border-orange-500/60" : "bg-gray-800 text-gray-300 border border-gray-700 hover:border-orange-600/40"}`}
              >
                <svg
                  className="w-3.5 h-3.5 text-orange-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 3L5 7v10l7 4 7-4V7l-7-4z" strokeWidth="2" />
                  <path d="M12 11v10M5 7l7 4 7-4" strokeWidth="2" />
                </svg>
                <span className="font-medium">Iso</span>
              </button>
            </div>
            <button
              onClick={handleGridToggle}
              className={`flex items-center justify-center gap-1.5 px-2.5 py-0.5 text-xs rounded-md transition-all shadow-sm ${gridVisible ? "bg-gradient-to-br from-indigo-500/30 to-indigo-600/20 text-indigo-200 border border-indigo-500/60" : "bg-gray-800 text-gray-400 border border-gray-700 hover:border-indigo-600/40"}`}
            >
              <svg
                className="w-3.5 h-3.5 text-indigo-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M3 12h18M12 3v18M8 3v18M16 3v18M3 8h18M3 16h18"
                  strokeWidth="1.5"
                />
              </svg>
              <span className="font-medium">Grids</span>
            </button>
          </div>
        </div>

        <div className="w-px h-12 bg-gradient-to-b from-transparent via-gray-600 to-transparent"></div>
        <div className="flex items-center gap-2">
          <div className="text-[10px] text-gray-500 font-semibold writing-mode-vertical h-12 flex items-center tracking-wide">
            ZOOM
          </div>
          <div className="flex items-center gap-0 bg-gradient-to-br from-slate-800 via-slate-850 to-slate-900 rounded-lg border border-slate-600/50 p-0.5 shadow-xl backdrop-blur-sm">
            <button
              onClick={() => {
                const currentZoom =
                  useDrawingStore.getState().viewTransform.scale;
                const newZoom = Math.max(0.1, currentZoom - 0.1);
                useDrawingStore
                  .getState()
                  .setZoomUnified(newZoom, { origin: "center" });
              }}
              className="group flex items-center justify-center w-9 h-9 text-gray-400 hover:text-white hover:bg-gradient-to-br hover:from-red-600/20 hover:to-orange-600/20 rounded-md transition-all duration-200 font-bold text-xl border border-transparent hover:border-orange-500/30 hover:shadow-lg hover:shadow-orange-500/20"
              title="Zoom Out (-10%)"
            >
              <span className="transform group-hover:scale-110 transition-transform">
                −
              </span>
            </button>
            <input
              type="text"
              value={isZoomInputFocused ? zoomInputValue : zoomLevel}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, "");
                setZoomInputValue(val);
              }}
              onFocus={() => {
                setIsZoomInputFocused(true);
                setZoomInputValue(String(zoomLevel));
              }}
              onBlur={() => {
                setIsZoomInputFocused(false);
                if (zoomInputValue !== "") {
                  const num = parseInt(zoomInputValue, 10);
                  if (!isNaN(num)) {
                    const clampedZoom = Math.max(10, Math.min(400, num));
                    useDrawingStore
                      .getState()
                      .setZoomUnified(clampedZoom / 100, { origin: "center" });
                  }
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  (e.target as HTMLInputElement).blur();
                }
                if (e.key === "Escape") {
                  setIsZoomInputFocused(false);
                  setZoomInputValue("");
                  (e.target as HTMLInputElement).blur();
                }
              }}
              className="w-[50px] h-9 px-1 bg-gradient-to-b from-slate-900 to-slate-950 border-x border-slate-700/50 text-white text-sm font-bold tabular-nums shadow-inner text-center outline-none focus:ring-1 focus:ring-blue-500"
              title="Enter zoom level (10-400%)"
            />
            <div className="flex items-center justify-center w-7 h-9 text-gray-400 text-[10px] font-semibold">
              %
            </div>
            <button
              onClick={() => {
                const currentZoom =
                  useDrawingStore.getState().viewTransform.scale;
                const newZoom = Math.min(4.0, currentZoom + 0.1);
                useDrawingStore
                  .getState()
                  .setZoomUnified(newZoom, { origin: "center" });
              }}
              className="group flex items-center justify-center w-9 h-9 text-gray-400 hover:text-white hover:bg-gradient-to-br hover:from-emerald-600/20 hover:to-green-600/20 rounded-md transition-all duration-200 font-bold text-xl border border-transparent hover:border-green-500/30 hover:shadow-lg hover:shadow-green-500/20"
              title="Zoom In (+10%)"
            >
              <span className="transform group-hover:scale-110 transition-transform">
                +
              </span>
            </button>
          </div>
        </div>
        <div className="w-px h-12 bg-gradient-to-b from-transparent via-gray-600 to-transparent"></div>
        <div className="flex items-center gap-2">
          <div className="text-[10px] text-gray-500 font-semibold writing-mode-vertical h-12 flex items-center tracking-wide">
            AXIS
          </div>
          <div className="flex flex-col gap-1">
            <button
              onClick={() => setShowGlobalAxis(!showGlobalAxis)}
              className={`flex items-center gap-1.5 px-2.5 py-0.5 text-xs rounded-md transition-all shadow-sm ${showGlobalAxis ? "bg-gradient-to-br from-amber-500/30 to-amber-600/20 text-amber-200 border border-amber-500/60" : "bg-gray-800 text-gray-400 border border-gray-700 hover:border-amber-600/40"}`}
            >
              <svg
                className="w-3.5 h-3.5 text-amber-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M12 3v18M3 12h18"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M12 3l2 2M12 3l-2 2M21 12l-2 2M21 12l-2-2"
                  strokeWidth="2"
                />
              </svg>
              <span className="font-medium">Global</span>
            </button>
            <button
              onClick={() => setShowLocalAxis(!showLocalAxis)}
              className={`flex items-center gap-1.5 px-2.5 py-0.5 text-xs rounded-md transition-all shadow-sm ${showLocalAxis ? "bg-gradient-to-br from-amber-500/30 to-amber-600/20 text-amber-200 border border-amber-500/60" : "bg-gray-800 text-gray-400 border border-gray-700 hover:border-amber-600/40"}`}
            >
              <svg
                className="w-3.5 h-3.5 text-amber-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <circle cx="12" cy="12" r="2" fill="currentColor" />
                <path
                  d="M12 10V7M12 17v-3M10 12H7M17 12h-3"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              <span className="font-medium">Local</span>
            </button>
          </div>
        </div>
        <div className="w-px h-12 bg-gradient-to-b from-transparent via-gray-600 to-transparent"></div>

        <div className="flex items-center gap-2">
          <div className="text-[10px] text-gray-500 font-semibold writing-mode-vertical h-12 flex items-center tracking-wide">
            SCALE
          </div>

          <button
            onClick={() => toggleAutoScale()}
            className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md transition-all
                        ${
                          autoScale
                            ? "bg-green-500/30 text-green-200 border border-green-500/60"
                            : "bg-gray-800 text-gray-400 border border-gray-700 hover:border-green-600/40"
                        }`}
          >
            <span className="font-medium">Auto Scale</span>
          </button>

          <select
            disabled={autoScale}
            value={scaleRange}
            onChange={(e) => setScaleRange(Number(e.target.value))}
            className={`w-full bg-gray-800 text-gray-200 border rounded-md px-6 py-2 text-xs
                        ${autoScale ? "opacity-40 cursor-not-allowed" : ""}
                      `}
          >
            {SCALE_RANGES.map((range) => (
              <option key={range.max} value={range.max}>
                {range.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default ViewMenu;
